import { supabase } from '@/lib/supabase'
import type { CanonicalEventRow } from '@/lib/eventbrite'
import type {
  Attendee,
  Event,
  FormSubmission,
  MatchmakingForm,
  MatchOutcome,
  MatchPairRow,
  MatchRun,
  MatchRunSummary,
  TopSelectedRow,
  Json,
} from '@/types/database'

/**
 * Admin data access.
 *
 * Every query here relies on RLS for scoping rather than adding its own
 * organization filter: the database already restricts each row to the caller's
 * organizations, and a redundant `.eq('organization_id', …)` in the client would
 * read like the security control when it is not one.
 *
 * Errors are thrown rather than returned so callers can use one try/catch; the
 * message is Supabase's, which is safe to show an authenticated admin.
 */

function unwrap<T>({
  data,
  error,
}: {
  data: T
  error: { message: string } | null
}): NonNullable<T> {
  if (error) throw new Error(error.message)
  if (data === null || data === undefined) throw new Error('No data returned')
  return data
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export async function listEvents(): Promise<Event[]> {
  return unwrap(
    await supabase
      .from('matchmaker_events')
      .select('*')
      .is('archived_at', null)
      .order('starts_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
  )
}

export async function getEvent(eventId: string): Promise<Event> {
  return unwrap(await supabase.from('matchmaker_events').select('*').eq('id', eventId).single())
}

export async function createEvent(input: {
  organizationId: string
  name: string
  venue?: string | null
  startsAt?: string | null
}): Promise<Event> {
  // The matchmaking form row is created by a trigger on the event insert, so
  // there is no second call to get half-done here.
  return unwrap(
    await supabase
      .from('matchmaker_events')
      .insert({
        organization_id: input.organizationId,
        name: input.name,
        venue: input.venue ?? null,
        starts_at: input.startsAt ?? null,
      })
      .select()
      .single()
  )
}

export interface EventImportResult {
  created: boolean
  event_id: string
  name: string
}

/**
 * Creates a local event from an Eventbrite one.
 *
 * Re-importing an event that is already linked never overwrites local edits —
 * an organiser who renamed it or set a venue keeps those. `created` says which
 * happened.
 */
export async function importEventbriteEvent(
  organizationId: string,
  event: CanonicalEventRow
): Promise<EventImportResult> {
  const { data, error } = await supabase.rpc('import_eventbrite_event', {
    p_organization_id: organizationId,
    p_event: event as unknown as Json,
  })
  if (error) throw new Error(error.message)
  return data
}

export async function updateEvent(eventId: string, patch: Partial<Event>): Promise<Event> {
  return unwrap(
    await supabase.from('matchmaker_events').update(patch).eq('id', eventId).select().single()
  )
}

export interface ResetEventResult {
  submissions_removed: number
  runs_removed: number
  attendees_removed: number
}

/**
 * Clears an event's submissions and results, returning the form to draft.
 *
 * With `removeAttendees`, deletes the attendee list too. Owner-only, enforced
 * in the database — the UI hides it from admins, but that is a courtesy, not
 * the control.
 */
export async function resetEvent(
  eventId: string,
  removeAttendees: boolean
): Promise<ResetEventResult> {
  const { data, error } = await supabase.rpc('reset_event', {
    p_event_id: eventId,
    p_remove_attendees: removeAttendees,
  })
  if (error) throw new Error(error.message)
  return data
}

export interface RemoveAttendeesResult {
  attendees_removed: number
  submissions_removed: number
  runs_removed: number
}

/**
 * Deletes an event's attendees, leaving the form exactly as it is.
 *
 * Submissions and match results reference attendees with ON DELETE CASCADE, so
 * they go too — a foreign-key consequence, not a choice. The counts come back
 * so the UI can say so rather than let it be discovered afterwards.
 */
export async function removeEventAttendees(eventId: string): Promise<RemoveAttendeesResult> {
  const { data, error } = await supabase.rpc('remove_event_attendees', {
    p_event_id: eventId,
  })
  if (error) throw new Error(error.message)
  return data
}

export async function archiveEvent(eventId: string): Promise<void> {
  const { error } = await supabase
    .from('matchmaker_events')
    .update({ archived_at: new Date().toISOString(), status: 'archived' })
    .eq('id', eventId)
  if (error) throw new Error(error.message)
}

export interface EventbriteStatus {
  connected: boolean
  eventbrite_organization_id?: string
  eventbrite_organization_name?: string | null
  connected_at?: string
}

/** Which Eventbrite account an organization is connected to. Never the token. */
export async function getEventbriteStatus(organizationId: string): Promise<EventbriteStatus> {
  const { data, error } = await supabase.rpc('get_organization_eventbrite_status', {
    p_organization_id: organizationId,
  })
  if (error) throw new Error(error.message)
  return data
}

export async function disconnectEventbrite(organizationId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_organization_eventbrite', {
    p_organization_id: organizationId,
  })
  if (error) throw new Error(error.message)
}

// ---------------------------------------------------------------------------
// Attendees
// ---------------------------------------------------------------------------

export async function listAttendees(eventId: string): Promise<Attendee[]> {
  return unwrap(
    await supabase
      .from('matchmaker_attendees')
      .select('*')
      .eq('event_id', eventId)
      .order('matchmaking_id', { ascending: true })
  )
}

export async function updateAttendee(
  attendeeId: string,
  patch: Partial<Attendee>
): Promise<Attendee> {
  return unwrap(
    await supabase.from('matchmaker_attendees').update(patch).eq('id', attendeeId).select().single()
  )
}

export async function createAttendee(input: {
  eventId: string
  matchmakingId: string
  firstName: string
  lastName?: string | null
  email?: string | null
  phone?: string | null
  gender?: string | null
}): Promise<Attendee> {
  return unwrap(
    await supabase
      .from('matchmaker_attendees')
      .insert({
        event_id: input.eventId,
        // organization_id is stamped from the parent event by a trigger.
        matchmaking_id: input.matchmakingId,
        first_name: input.firstName,
        last_name: input.lastName ?? null,
        email: input.email ?? null,
        phone: input.phone ?? null,
        gender: input.gender ?? null,
        source: 'manual',
      })
      .select()
      .single()
  )
}

export async function deleteAttendee(attendeeId: string): Promise<void> {
  const { error } = await supabase.from('matchmaker_attendees').delete().eq('id', attendeeId)
  if (error) throw new Error(error.message)
}

/** Lowest unused 3-digit number for an event, matching the SQL allocator. */
export function nextMatchmakingId(attendees: readonly Attendee[]): string {
  const taken = new Set(attendees.map((a) => a.matchmaking_id))
  for (let candidate = 100; candidate <= 999; candidate++) {
    const id = String(candidate).padStart(3, '0')
    if (!taken.has(id)) return id
  }
  return ''
}

// ---------------------------------------------------------------------------
// Matchmaking form
// ---------------------------------------------------------------------------

export async function getForm(eventId: string): Promise<MatchmakingForm | null> {
  const { data, error } = await supabase
    .from('matchmaker_forms')
    .select('*')
    .eq('event_id', eventId)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data
}

export async function updateForm(
  formId: string,
  patch: Partial<MatchmakingForm>
): Promise<MatchmakingForm> {
  return unwrap(
    await supabase.from('matchmaker_forms').update(patch).eq('id', formId).select().single()
  )
}

/** Hashes server-side; the plaintext is never stored or returned. */
export async function setFormPasscode(formId: string, passcode: string): Promise<void> {
  const { error } = await supabase.rpc('set_form_passcode', {
    p_form_id: formId,
    p_passcode: passcode,
  })
  if (error) throw new Error(error.message)
}

/**
 * Invalidates every outstanding form session for this event.
 *
 * The key itself never comes back — an admin has no use for it, and returning
 * it would put it in a browser. Only the timestamp is returned.
 */
export async function rotateFormSessionSecret(formId: string): Promise<string> {
  const { data, error } = await supabase.rpc('rotate_form_session_secret', {
    p_form_id: formId,
  })
  if (error) throw new Error(error.message)
  return data
}

export async function getFormSessionRotatedAt(formId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_form_session_rotated_at', {
    p_form_id: formId,
  })
  if (error) throw new Error(error.message)
  return data
}

export async function publishForm(formId: string): Promise<MatchmakingForm> {
  return await updateForm(formId, {
    status: 'published',
    published_at: new Date().toISOString(),
    closed_at: null,
  })
}

export async function closeForm(formId: string): Promise<MatchmakingForm> {
  return await updateForm(formId, { status: 'closed', closed_at: new Date().toISOString() })
}

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

export interface SubmissionWithPicks extends FormSubmission {
  /** Aliased in the select, so a table rename cannot change this key. */
  selections: { selected_attendee_id: string; position: number | null }[]
}

/**
 * Every submission, newest first.
 *
 * A person may appear more than once: submitting again adds an entry rather
 * than replacing one. The matchmaker reads only their newest, and the organiser
 * deletes the rest — so all of them have to be listed, or the entry that is
 * about to decide someone's matches could be one nobody has seen.
 */
export async function listSubmissions(eventId: string): Promise<SubmissionWithPicks[]> {
  return unwrap(
    await supabase
      .from('matchmaker_form_submissions')
      .select('*, selections:matchmaker_form_selections(selected_attendee_id, position)')
      .eq('event_id', eventId)
      .order('submitted_at', { ascending: false })
  ) as unknown as SubmissionWithPicks[]
}

export interface DeleteSubmissionResult {
  picks_removed: number
  /** Entries that person has left. Zero means they are out of the matchmaking. */
  remaining_for_submitter: number
  /** True when a match run exists, so its results now predate this deletion. */
  results_stale: boolean
}

/**
 * Deletes one submission — the entry, not the person.
 *
 * Someone who submitted twice keeps the entry before this one, and it takes
 * over as the answer the matchmaker reads. Match runs are left alone;
 * `results_stale` says whether one is now out of date.
 */
export async function deleteSubmission(submissionId: string): Promise<DeleteSubmissionResult> {
  const { data, error } = await supabase.rpc('delete_submission', {
    p_submission_id: submissionId,
  })
  if (error) throw new Error(error.message)
  return data
}

// ---------------------------------------------------------------------------
// Matchmaking
// ---------------------------------------------------------------------------

export async function runMatchmaking(eventId: string): Promise<MatchRunSummary> {
  const { data, error } = await supabase.rpc('run_matchmaking', { p_event_id: eventId })
  if (error) throw new Error(error.message)
  return data
}

export async function getLatestRun(eventId: string): Promise<MatchRun | null> {
  const { data, error } = await supabase
    .from('matchmaker_runs')
    .select('*')
    .eq('event_id', eventId)
    .order('run_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data
}

export async function getMatchPairs(eventId: string): Promise<MatchPairRow[]> {
  const { data, error } = await supabase.rpc('get_match_pairs', { p_event_id: eventId })
  if (error) throw new Error(error.message)
  return data
}

export async function getTopSelected(eventId: string, limit = 5): Promise<TopSelectedRow[]> {
  const { data, error } = await supabase.rpc('get_top_selected', {
    p_event_id: eventId,
    p_limit: limit,
  })
  if (error) throw new Error(error.message)
  return data
}

export interface GenderMatchRateRow {
  gender: string
  submitters: number
  matched: number
  match_rate: number
}

/** Match rate per gender for the latest run. Denominator is submitters. */
export async function getMatchRateByGender(eventId: string): Promise<GenderMatchRateRow[]> {
  const { data, error } = await supabase.rpc('get_match_rate_by_gender', { p_event_id: eventId })
  if (error) throw new Error(error.message)
  return data
}

export async function listOutcomes(runId: string): Promise<MatchOutcome[]> {
  return unwrap(await supabase.from('matchmaker_outcomes').select('*').eq('run_id', runId))
}
